import { useMemo } from "react";
import { useFetchActions, type Action } from "@core/features/actions";
import { usePlatformStore } from "@platform/store";

/**
 * Hook to get a user action related to a specific meeting.
 *
 * @param meetingId - The meeting ID to find related action for
 * @param actionTypeSlug - The action type slug to look for (e.g., "meeting_survey", "meeting_script")
 * @param programId - The program ID to fetch actions for
 * @returns The matching action or undefined
 */
export function useMeetingRelatedUserPhaseAction(
  meetingId: string | null,
  actionTypeSlug: string,
  programId: number
): Action | undefined {
  const user = usePlatformStore((s) => s.user);

  const { data: actionsData } = useFetchActions({
    programId,
    user,
    isEnabled: !!user && !!programId,
  });

  const matchingAction = useMemo(
    () =>
      actionsData?.actions
        ?.filter((action) => action.action_type?.slug === actionTypeSlug)
        .find(
          (action) =>
            action.binding_object_id === parseInt(meetingId || "0", 10)
        ),
    [actionsData?.actions, meetingId, actionTypeSlug]
  );

  return matchingAction;
}

export default useMeetingRelatedUserPhaseAction;
